// ccp/src/lib/connectData.js
//
// Maps a live amazon-connect-streams Contact into the display shape the console
// renders. Real sources only — no mock fallback. Demo mode (no active contact)
// is handled in ContactContext by keeping MOCK_CONTACT as the initial display.
//
// STEP 1 scope: populate `meta` (incl. channel) from the Streams contact, and keep
// the identity / aiSummary / intents reads that already existed. Assist fields
// (transcript, sentiment, suggestions, kb, history, alerts) are returned EMPTY here
// and get wired in their own steps — empty renders as "nothing", never fake data.

// 16 Oct proof call (contact 8d214679): a queued callback is a phone call, but Streams types
// it 'queue_callback'. Unmapped, it became 'QUEUE_CALLBACK', so the live transcript,
// sentiment, coaching and knowledge never ran on callbacks, and the dial and wrap-up gates
// did not see a live call. It is voice.
const CHANNEL_MAP = { voice: 'VOICE', queue_callback: 'VOICE', chat: 'CHAT', task: 'TASK' };

// connect.ContactType -> our UI channel label. This is the omnichannel switch.
export function mapChannel(contactType) {
  if (!contactType) return 'VOICE';
  const key = String(contactType).toLowerCase();
  return CHANNEL_MAP[key] || key.toUpperCase();
}

// Never let a Streams getter throw — return a fallback instead.
function safe(fn, fallback = null) {
  try { return fn(); } catch { return fallback; }
}

const attrVal = (attrs, name) => attrs?.[name]?.value;

// Call meta straight from Streams — no flow attributes required for these.
export function buildMeta(contact) {
  const channel = mapChannel(safe(() => contact.getType()));
  const queue = safe(() => contact.getQueue()?.name);
  const contactId = safe(() => contact.getContactId());

  // Caller's number (voice). Chat has no phone endpoint; stays null.
  const initialConn = safe(() => contact.getInitialConnection());
  const phone = safe(() => initialConn?.getEndpoint?.()?.phoneNumber) || null;

  // Dialed number (DNIS) — set as a contact attribute on the inbound flow.
  const attrs = safe(() => contact.getAttributes(), {}) || {};
  const dnis = attrVal(attrs, 'tenantDnis') || attrVal(attrs, 'dnis') || null;

  return {
    contactId,
    queue: queue || null,
    channel,
    startTime: new Date().toLocaleTimeString('en-AU', { hour12: false }),
    dnis,
    phone,
  };
}

// Full display object for a live contact. Identity + handover come from contact
// attributes (the flow must Set these — see the attribute contract); meta/channel
// come from Streams. Assist fields are empty until their steps wire real sources.
export function buildDisplay(contact) {
  const attrs = safe(() => contact.getAttributes(), {}) || {};
  const meta = buildMeta(contact);

  const intentsRaw = attrVal(attrs, 'detectedIntents');
  const intents = intentsRaw
    ? intentsRaw.split(',').map((s) => s.trim()).filter(Boolean)
    : [];

  return {
    customer: {
      name: attrVal(attrs, 'customerName') || 'Unknown caller',
      account: attrVal(attrs, 'accountId') || '—',
      plan: attrVal(attrs, 'plan') || '—',
      phone: meta.phone || '—',
    },
    meta,
    aiHandled: attrVal(attrs, 'aiHandled') === 'true',
    aiHandledSeconds: Number(attrVal(attrs, 'aiHandledSeconds')) || 0,
    intents,
    aiSummary: attrVal(attrs, 'aiSummaryFull') || attrVal(attrs, 'aiSummary') || '',   // 2026-10-13: model summary first

    // ---- wired in later steps; empty = no data shown (never mock) ----
    history: [],            // Step 4 — knktr-contacts-history
    transcript: [],         // Step 6 — Contact Lens (voice) / ChatSession (chat)
    sentiment: null,        // Step 6 — Contact Lens
    suggestedResponses: [], // Step 5 — Q in Connect
    kbArticles: [],         // Step 5 — Q in Connect
    piiDetected: null,      // Step 6 — Contact Lens (gated on flow PII redaction)
    contextAlerts: [],      // Step 4 — derived from history + sentiment
  };
}
